import { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { text, border as borderToken, interactive, spacing, borderRadius, controlFontSize } from "@atlas-int/tokens";
import InfoTooltipTrigger from "./settingsPanel/InfoTooltipTrigger";

const SectionContainer = styled.div`
  margin: 0;
  padding: ${spacing[4]} ${spacing[6]};
  border-top: 1px solid ${borderToken.subtle};
`;

const SectionLabel = styled.legend`
  font-size: ${controlFontSize.sm};
  font-weight: 600;
  color: ${text.primary};
  padding: 0;
  margin-bottom: ${spacing[3]};
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
`;

const thumbSize = spacing[6];
const trackHeight = spacing[1];

const Slider = styled.input`
  -webkit-appearance: none;
  appearance: none;
  flex: 1;
  height: ${trackHeight};
  border-radius: ${borderRadius.base};
  padding: 0;
  margin: 0;
  background: transparent;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: ${thumbSize};
    height: ${thumbSize};
    background-color: ${interactive.default};
    border-radius: ${borderRadius.full};
    border: 2px solid ${text.onDark};
    margin-top: calc((${trackHeight} - ${thumbSize}) / 2);
    position: relative;
    z-index: 1;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: ${thumbSize};
    height: ${thumbSize};
    background-color: ${interactive.default};
    border-radius: ${borderRadius.full};
    border: 1px solid ${text.onDark};
    cursor: pointer;
  }

  &::-webkit-slider-runnable-track {
    height: ${trackHeight};
    border-radius: ${borderRadius.md};
    border: 1px solid ${borderToken.control};
    background: linear-gradient(
      to right,
      ${interactive.default} 0%,
      ${interactive.default} var(--value),
      ${borderToken.control} var(--value),
      ${borderToken.control} 100%
    );
  }

  &::-moz-range-track {
    height: ${trackHeight};
    border-radius: ${borderRadius.md};
    border: 1px solid ${borderToken.control};
    background: linear-gradient(
      to right,
      ${interactive.default} 0%,
      ${interactive.default} var(--value),
      ${borderToken.control} var(--value),
      ${borderToken.control} 100%
    );
  }
`;

const ValueInput = styled.input`
  width: ${spacing[12]};
  padding: ${spacing[1]} ${spacing[2]};
  font-size: ${controlFontSize.sm};
  color: ${text.primary};
  border: 1px solid ${borderToken.control};
  border-radius: ${borderRadius.base};
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${interactive.default};
  }

  /* Hide spin buttons */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

interface RcaSliderProps {
  /** Current RCA threshold value */
  value: number;
  /** Called when the value is committed (on mouse up / input blur) */
  onChange: (value: number) => void;
  /** Called when the user finishes dragging the slider */
  onChangeComplete?: () => void;
  /** Tooltip explanation for the RCA Threshold label */
  tooltip?: string;
  /** Minimum value (default: 0) */
  min?: number;
  /** Maximum value (default: 5) */
  max?: number;
  /** Step size (default: 0.1) */
  step?: number;
}

const RcaSlider = ({
  value,
  onChange,
  onChangeComplete,
  tooltip,
  min = 0,
  max = 5,
  step = 0.1,
}: RcaSliderProps) => {
  // Local state for responsive dragging without URL round-trips
  const [localValue, setLocalValue] = useState(value);

  // Sync local state when the prop changes (e.g. URL navigation, reset)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const fillPercent = `${(localValue / max) * 100}%`;

  return (
    <SectionContainer>
      <SectionLabel>
        RCA Threshold
        {tooltip && <InfoTooltipTrigger text={tooltip} />}
      </SectionLabel>
      <SliderRow>
        <Slider
          type="range"
          min={min}
          max={max}
          step={step}
          value={localValue}
          onChange={(e) => setLocalValue(parseFloat(e.target.value))}
          onMouseUp={() => {
            onChange(localValue);
            onChangeComplete?.();
          }}
          onTouchEnd={() => {
            onChange(localValue);
            onChangeComplete?.();
          }}
          style={{ "--value": fillPercent } as React.CSSProperties}
        />
        <ValueInput
          type="number"
          value={localValue}
          onChange={(e) => {
            const parsed = parseFloat(e.target.value);
            if (!Number.isNaN(parsed)) setLocalValue(parsed);
          }}
          onBlur={() => onChange(localValue)}
          step={step}
          min={min}
          max={max}
        />
      </SliderRow>
    </SectionContainer>
  );
};
export default RcaSlider;
